import { Link } from "react-router-dom";
import { fmtINR, imageUrl, buildWhatsAppLink } from "@/lib/api";
import { MapPin, MessageCircle } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=1200&auto=format&fit=crop";

export default function ListingCard({ listing, index = 0 }) {
  const cover = listing.images?.[0];
  const src = cover ? (cover.startsWith("http") ? cover : imageUrl(cover)) : FALLBACK;

  const waHref = buildWhatsAppLink(
    `Hello HummingStayz! I'm interested in *${listing.title}* (${listing.area}, ${listing.city}).`
  );

  return (
    <div
      className="surface-card overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(0,0,0,0.08)] animate-fade-up flex flex-col"
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`listing-card-${listing.id}`}
    >
      <Link to={`/listings/${listing.id}`} className="block" data-testid={`listing-link-${listing.id}`}>
        <div className="relative h-64 overflow-hidden">
          <img
            src={src}
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute top-4 left-4 flex gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-medium backdrop-blur-md" style={{ background: "rgba(255,255,255,0.85)", color: "var(--forest)" }}>
              Long Stay
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: "var(--forest)", color: "#fff" }}>
              Min {listing.min_stay_nights} nights
            </span>
          </div>
        </div>

        <div className="p-6 pb-4">
          <h3 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--text-1)" }}>
            {listing.title}
          </h3>
          <div className="mt-1 flex items-center gap-1 text-sm" style={{ color: "var(--text-2)" }}>
            <MapPin className="w-3.5 h-3.5" /> {listing.area}, {listing.city}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
            {[
              ["Per Night", fmtINR(listing.price_per_night)],
              ["Weekly", fmtINR(listing.price_weekly)],
              ["15 Nights", fmtINR(listing.price_15_nights)],
              ["Monthly", fmtINR(listing.price_monthly)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg p-3" style={{ background: "var(--surface-alt)" }}>
                <div className="overline text-[10px]">{label}</div>
                <div className="mt-1 font-medium text-[color:var(--forest)]">{value}</div>
              </div>
            ))}
          </div>

          {listing.addons?.filter((a) => a.enabled).length > 0 && (
            <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--line)" }}>
              <div className="overline text-[10px] mb-2">Add-ons available</div>
              <div className="flex flex-wrap gap-1.5">
                {listing.addons.filter((a) => a.enabled).map((a) => (
                  <span key={a.key} className="text-xs px-2.5 py-1 rounded-full" style={{ background: "rgba(198,125,99,0.12)", color: "var(--terracotta-hover)" }}>
                    {a.key.replace("_", " ")}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Link>

      <div className="px-6 pb-6 pt-2 mt-auto flex items-center gap-2">
        <Link
          to={`/listings/${listing.id}`}
          className="btn-pill btn-outline-dark flex-1 !py-2.5 text-xs"
          data-testid={`view-listing-${listing.id}`}
        >
          View details
        </Link>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="btn-pill !py-2.5 !px-4 text-xs"
          style={{ background: "#25D366", color: "#0B1F12" }}
          data-testid={`whatsapp-enquire-${listing.id}`}
          aria-label="Enquire on WhatsApp"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Enquire
        </a>
      </div>
    </div>
  );
}
